import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiTrash2, FiClock, FiBarChart2, FiChevronDown, FiChevronUp, FiCpu, FiSliders } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import CFTCDisclaimer from '../components/CFTCDisclaimer';
import TearSheet, { TearSheetData } from '../components/tearsheet/TearSheet';
import { DEMO_STRATEGIES, type DemoStrategy } from '../data/demoData';

interface StrategyVersion {
  id: number;
  version_num: number;
  code_snapshot_url: string | null;
  notes: string | null;
  created_at: string;
}

interface BacktestResult {
  id: number;
  strategy_id: number;
  version_id: number | null;
  run_at: string;
  parameters: Record<string, unknown> | null;
  metrics: {
    sharpe?: number;
    cagr?: number;
    max_drawdown?: number;
    total_return?: number;
    win_rate?: number;
    profit_factor?: number;
    sortino?: number;
    calmar?: number;
    deflated_sharpe?: number;
    information_ratio?: number;
    omega?: number;
    tail_ratio?: number;
    treynor?: number;
    beta?: number;
    alpha?: number;
    skewness?: number;
    kurtosis?: number;
    var_95?: number;
    cvar_95?: number;
  } | null;
  equity_curve_url: string | null;
  trades_url: string | null;
  notebook_path: string | null;
  duration_seconds: number | null;
  /** Daily returns for equity curve */
  returns?: number[];
  /** Benchmark returns for comparison */
  benchmark_returns?: number[];
  /** Rolling statistics */
  rolling_sharpe?: number[];
  rolling_sortino?: number[];
  rolling_volatility?: number[];
  rolling_beta?: number[];
  /** Drawdown data */
  underwater?: number[];
  max_drawdown_duration_days?: number;
  recovery_factor?: number;
  /** Monthly returns grid */
  monthly_returns?: Record<number, Record<number, number>>;
}

type MetricsMode = 'basic' | 'standard' | 'forensic';

interface Strategy {
  id: number;
  name: string;
  description: string | null;
  project_id: number | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  version_count: number;
  backtest_count: number;
  latest_metrics: Record<string, number> | null;
  versions: StrategyVersion[];
}

const PYTHON_KEYWORDS = new Set([
  'import', 'from', 'as', 'def', 'return', 'class', 'if', 'elif', 'else',
  'for', 'while', 'in', 'not', 'and', 'or', 'is', 'None', 'True', 'False',
  'with', 'try', 'except', 'finally', 'raise', 'yield', 'lambda', 'pass',
  'break', 'continue',
]);

const highlightPython = (code: string) => {
  const tokenPattern = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|#.*$|\b\d+\.?\d*\b|\b[a-zA-Z_]\w*\b|[^\s\w]+|\s+)/g;

  return code.trimEnd().split('\n').map((line, lineIdx) => {
    const trimmed = line.trimStart();
    if (!trimmed) return <div key={lineIdx}>&nbsp;</div>;
    if (trimmed.startsWith('#')) {
      return <div key={lineIdx}><span className="text-gray-500">{line}</span></div>;
    }
    if (trimmed.startsWith('"""') || trimmed.startsWith("'''")) {
      return <div key={lineIdx}><span className="text-green-400">{line}</span></div>;
    }

    const tokens: JSX.Element[] = [];
    const re = new RegExp(tokenPattern.source, 'g');
    let match;
    let k = 0;
    let afterDef = false;

    while ((match = re.exec(line)) !== null) {
      const t = match[0];
      if (t.startsWith('#')) {
        tokens.push(<span key={k++} className="text-gray-500">{t}</span>);
        afterDef = false;
      } else if (t.startsWith('"') || t.startsWith("'")) {
        tokens.push(<span key={k++} className="text-green-400">{t}</span>);
        afterDef = false;
      } else if (PYTHON_KEYWORDS.has(t)) {
        tokens.push(<span key={k++} className="text-purple-400">{t}</span>);
        afterDef = t === 'def';
      } else if (afterDef && /^[a-zA-Z_]\w*$/.test(t)) {
        tokens.push(<span key={k++} className="text-blue-400">{t}</span>);
        afterDef = false;
      } else if (/^\d+\.?\d*$/.test(t)) {
        tokens.push(<span key={k++} className="text-yellow-400">{t}</span>);
        afterDef = false;
      } else if (/^\s+$/.test(t)) {
        tokens.push(<span key={k++}>{t}</span>);
      } else {
        tokens.push(<span key={k++} className="text-white">{t}</span>);
        afterDef = false;
      }
    }

    return <div key={lineIdx}>{tokens}</div>;
  });
};

export default function StrategyDetailPage() {
  const { strategyId } = useParams<{ strategyId: string }>();
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [results, setResults] = useState<BacktestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedResult, setExpandedResult] = useState<number | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [metricsMode, setMetricsMode] = useState<MetricsMode>('standard');
  const [demoStrategy, setDemoStrategy] = useState<DemoStrategy | null>(null);
  const [demoTearSheet, setDemoTearSheet] = useState<TearSheetData | null>(null);
  const navigate = useNavigate();

  const isDemo = strategyId?.startsWith('demo-') ?? false;

  /**
   * Convert a BacktestResult to TearSheetData format.
   * Falls back to zeros for missing fields.
   */
  const convertToTearSheetData = (result: BacktestResult): TearSheetData | null => {
    if (!result.metrics) return null;
    const m = result.metrics;
    return {
      basic: {
        total_return: m.total_return ?? 0,
        cagr: m.cagr ? m.cagr / 100 : 0, // Convert percentage to decimal
        sharpe_ratio: m.sharpe ?? 0,
        sortino_ratio: m.sortino ?? 0,
        calmar_ratio: m.calmar ?? 0,
        max_drawdown: m.max_drawdown ? -Math.abs(m.max_drawdown) / 100 : 0,
        win_rate: m.win_rate ? m.win_rate / 100 : 0,
        profit_factor: m.profit_factor ?? 0,
      },
      advanced: {
        deflated_sharpe_ratio: m.deflated_sharpe ?? 0,
        information_ratio: m.information_ratio ?? 0,
        omega_ratio: m.omega ?? 0,
        tail_ratio: m.tail_ratio ?? 0,
        treynor_ratio: m.treynor ?? 0,
        beta: m.beta ?? 1,
        alpha: m.alpha ?? 0,
      },
      tail_risk: {
        skewness: m.skewness ?? 0,
        kurtosis: m.kurtosis ?? 3,
        var_95: m.var_95 ?? 0,
        cvar_95: m.cvar_95 ?? 0,
      },
      rolling: {
        sharpe: result.rolling_sharpe ?? [],
        sortino: result.rolling_sortino ?? [],
        volatility: result.rolling_volatility ?? [],
        beta: result.rolling_beta ?? [],
      },
      drawdown: {
        underwater: result.underwater ?? [],
        max_duration_days: result.max_drawdown_duration_days ?? 0,
        recovery_factor: result.recovery_factor ?? 0,
      },
      monthly_returns: result.monthly_returns ?? {},
      metadata: {
        start_date: new Date(result.run_at).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        trading_days: result.returns?.length ?? 0,
        trial_count: 1,
      },
      returns: result.returns,
      benchmark_returns: result.benchmark_returns,
    };
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    if (isDemo) {
      const demo = DEMO_STRATEGIES.find(s => s.id === strategyId);
      if (!demo) {
        navigate('/dashboard/strategies');
        return;
      }
      setDemoStrategy(demo);
      setDemoTearSheet(demo.tearSheet);
      setStrategy({
        id: 0,
        name: demo.name,
        description: demo.description,
        project_id: null,
        is_public: true,
        created_at: '2023-01-03T00:00:00Z',
        updated_at: '2025-01-03T00:00:00Z',
        version_count: 1,
        backtest_count: 1,
        latest_metrics: {
          sharpe: demo.tearSheet.basic.sharpe_ratio,
          cagr: demo.tearSheet.basic.cagr * 100,
          max_drawdown: Math.abs(demo.tearSheet.basic.max_drawdown) * 100,
          total_return: demo.tearSheet.basic.total_return * 100,
          win_rate: demo.tearSheet.basic.win_rate * 100,
          profit_factor: demo.tearSheet.basic.profit_factor,
        },
        versions: [],
      });
      setLoading(false);
      return;
    }

    fetchStrategy();
    fetchResults();
  }, [strategyId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStrategy = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies/${strategyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStrategy(data);
        setEditName(data.name);
        setEditDescription(data.description || '');
      } else if (response.status === 404) {
        navigate('/dashboard/strategies');
      }
    } catch (error) {
      console.error('Error fetching strategy:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies/${strategyId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const handleUpdateStrategy = async () => {
    if (!editName.trim()) return;

    setSaving(true);
    setUpdateError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies/${strategyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editName.trim(),
          description: editDescription.trim() || null
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setStrategy({ ...strategy!, ...updated });
        setIsEditing(false);
        setUpdateError(null);
      } else {
        const error = await response.json();
        setUpdateError(error.detail || 'Failed to update strategy');
      }
    } catch (error) {
      console.error('Error updating strategy:', error);
      setUpdateError('Failed to update strategy. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStrategy = async () => {
    if (!window.confirm('Are you sure you want to delete this strategy? All backtest results will be lost.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies/${strategyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        navigate('/dashboard/strategies');
      } else {
        alert('Failed to delete strategy');
      }
    } catch (error) {
      console.error('Error deleting strategy:', error);
      alert('Failed to delete strategy');
    }
  };

  const formatMetric = (value: number | undefined, prefix: string = '', suffix: string = ''): string => {
    if (value === undefined || value === null) return '-';
    return `${prefix}${value.toFixed(2)}${suffix}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!strategy) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-gray-400">Strategy not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Link
          to="/dashboard/strategies"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Strategies
        </Link>

        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-3 max-w-xl">
                {updateError && (
                  <div className="p-3 bg-red-900/30 border border-red-800/50 rounded-md">
                    <p className="text-sm text-red-400">{updateError}</p>
                  </div>
                )}
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setUpdateError(null);
                  }}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white text-xl font-semibold focus:outline-none focus:border-indigo-500"
                  autoFocus
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Add a description..."
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-300 focus:outline-none focus:border-indigo-500 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdateStrategy}
                    disabled={!editName.trim() || saving}
                    className="px-3 py-1.5 bg-white text-black text-sm font-medium rounded-md hover:bg-gray-100 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditName(strategy.name);
                      setEditDescription(strategy.description || '');
                      setUpdateError(null);
                    }}
                    className="px-3 py-1.5 text-gray-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold text-white">{strategy.name}</h1>
                  {!isDemo && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1 text-gray-500 hover:text-white rounded transition-colors"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                  )}
                  {isDemo && (
                    <span className="text-xs px-2 py-0.5 bg-yellow-900/30 text-yellow-400 rounded-full">
                      Demo
                    </span>
                  )}
                  {strategy.is_public && !isDemo && (
                    <span className="text-xs px-2 py-0.5 bg-blue-900/30 text-blue-400 rounded-full">
                      Public
                    </span>
                  )}
                </div>
                {strategy.description && (
                  <p className="text-gray-400 mt-1">{strategy.description}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  Created {formatDate(strategy.created_at)}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Metrics Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
              {(['basic', 'standard', 'forensic'] as MetricsMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setMetricsMode(mode)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    metricsMode === mode
                      ? 'bg-profit text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {/* Open in Lab button */}
            <Link
              to={`/dashboard/lab?strategy=${strategyId}`}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md text-sm transition-colors"
              title="Open in AI Lab"
            >
              <FiCpu className="w-4 h-4" />
              Lab
            </Link>

            {!isDemo && (
              <button
                onClick={handleDeleteStrategy}
                className="p-2 text-gray-500 hover:text-red-400 rounded transition-colors"
                title="Delete strategy"
              >
                <FiTrash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Metrics Summary */}
        {strategy.latest_metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">Sharpe Ratio</p>
              <p className={`text-xl font-semibold tabular-nums ${
                (strategy.latest_metrics.sharpe ?? 0) >= 1 ? 'text-profit' : 'text-gray-300'
              }`}>
                {formatMetric(strategy.latest_metrics.sharpe)}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">CAGR</p>
              <p className={`text-xl font-semibold tabular-nums ${
                (strategy.latest_metrics.cagr ?? 0) > 0 ? 'text-profit' : 'text-loss'
              }`}>
                {formatMetric(strategy.latest_metrics.cagr, '', '%')}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">Max Drawdown</p>
              <p className="text-xl font-semibold text-loss tabular-nums">
                {formatMetric(strategy.latest_metrics.max_drawdown, '-', '%')}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">Total Return</p>
              <p className={`text-xl font-semibold tabular-nums ${
                (strategy.latest_metrics.total_return ?? 0) > 0 ? 'text-profit' : 'text-loss'
              }`}>
                {formatMetric(strategy.latest_metrics.total_return, '', '%')}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">Win Rate</p>
              <p className="text-xl font-semibold text-gray-300 tabular-nums">
                {formatMetric(strategy.latest_metrics.win_rate, '', '%')}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
              <p className="text-xs text-gray-500 mb-1">Profit Factor</p>
              <p className={`text-xl font-semibold tabular-nums ${
                (strategy.latest_metrics.profit_factor ?? 0) > 1 ? 'text-profit' : 'text-loss'
              }`}>
                {formatMetric(strategy.latest_metrics.profit_factor)}
              </p>
            </div>
          </div>
        )}

        {/* CFTC Disclaimer */}
        <CFTCDisclaimer />

        {/* TearSheet for demo strategy */}
        {isDemo && demoTearSheet && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-white flex items-center gap-2">
                <FiSliders className="w-4 h-4" />
                Demo Backtest Analysis
              </h2>
              {demoStrategy?.highlight && (
                <span className="text-sm text-yellow-400">
                  {demoStrategy.highlight}
                </span>
              )}
            </div>
            <TearSheet
              data={demoTearSheet}
              strategyName={strategy?.name ?? ''}
              defaultAdvanced={metricsMode === 'forensic'}
              compact={metricsMode === 'basic'}
              forensic={metricsMode === 'forensic'}
            />
          </div>
        )}

        {/* TearSheet for latest backtest */}
        {!isDemo && results.length > 0 && (() => {
          const latestResult = results[0]; // Already sorted by run_at desc
          const tearSheetData = convertToTearSheetData(latestResult);
          if (!tearSheetData) return null;
          return (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium text-white flex items-center gap-2">
                  <FiSliders className="w-4 h-4" />
                  Latest Backtest Analysis
                </h2>
                <span className="text-sm text-gray-500">
                  {formatDate(latestResult.run_at)}
                </span>
              </div>
              <TearSheet
                data={tearSheetData}
                strategyName={strategy.name}
                defaultAdvanced={metricsMode === 'forensic'}
                compact={metricsMode === 'basic'}
                forensic={metricsMode === 'forensic'}
              />
            </div>
          );
        })()}

        {/* Demo Strategy Code */}
        {isDemo && demoStrategy?.code && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-8">
            <div className="flex items-center mb-4">
              <div className="flex space-x-2 mr-4">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <span className="text-gray-400 text-sm">
                {demoStrategy.id.replace('demo-', '').replace(/-/g, '_')}.ipynb
              </span>
            </div>
            <div className="bg-black/50 rounded-lg p-4">
              <div className="text-gray-500 text-sm mb-2">In [1]:</div>
              <div className="font-mono text-sm text-left">
                {highlightPython(demoStrategy.code)}
              </div>
            </div>
          </div>
        )}

        {/* Backtest Results (real strategies only) */}
        {!isDemo && (
          <div className="bg-gray-900 border border-gray-800 rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800">
              <h2 className="font-medium text-white flex items-center gap-2">
                <FiBarChart2 className="w-4 h-4" />
                Backtest Results
              </h2>
            </div>

            {results.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <FiBarChart2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-2">No backtest results yet</p>
                <p className="text-sm text-gray-500">
                  Run a backtest from your notebook to save results here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {results.map((result) => (
                  <div key={result.id} className="bg-gray-900">
                    <button
                      onClick={() => setExpandedResult(expandedResult === result.id ? null : result.id)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500 flex items-center gap-1.5 text-sm">
                          <FiClock className="w-3.5 h-3.5" />
                          {formatDate(result.run_at)}
                        </span>
                        {result.metrics && (
                          <div className="flex items-center gap-4 text-sm">
                            <span className={`tabular-nums ${
                              (result.metrics.sharpe ?? 0) >= 1 ? 'text-profit' : 'text-gray-400'
                            }`}>
                              Sharpe: {formatMetric(result.metrics.sharpe)}
                            </span>
                            <span className={`tabular-nums ${
                              (result.metrics.cagr ?? 0) > 0 ? 'text-profit' : 'text-loss'
                            }`}>
                              CAGR: {formatMetric(result.metrics.cagr, '', '%')}
                            </span>
                            <span className="text-loss tabular-nums">
                              DD: {formatMetric(result.metrics.max_drawdown, '-', '%')}
                            </span>
                          </div>
                        )}
                      </div>
                      {expandedResult === result.id ? (
                        <FiChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>

                    {expandedResult === result.id && (
                      <div className="px-5 pb-4 pt-0">
                        <div className="bg-gray-800/50 rounded-md p-4 space-y-3">
                          {result.duration_seconds && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500">Duration:</span>
                              <span className="text-gray-300">{formatDuration(result.duration_seconds)}</span>
                            </div>
                          )}
                          {result.notebook_path && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500">Notebook:</span>
                              <span className="text-gray-300 font-mono text-xs">{result.notebook_path}</span>
                            </div>
                          )}
                          {result.parameters && Object.keys(result.parameters).length > 0 && (
                            <div>
                              <p className="text-gray-500 text-sm mb-1">Parameters:</p>
                              <pre className="text-xs text-gray-400 bg-gray-900 rounded p-2 overflow-x-auto">
                                {JSON.stringify(result.parameters, null, 2)}
                              </pre>
                            </div>
                          )}
                          {result.metrics && (
                            <div>
                              <p className="text-gray-500 text-sm mb-1">All Metrics:</p>
                              <pre className="text-xs text-gray-400 bg-gray-900 rounded p-2 overflow-x-auto">
                                {JSON.stringify(result.metrics, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
